<template>
    <div class="chart-widget">
        <div class="widget-header">
            <div class="left">
                <i
                    class="material-icons drag-handle"
                    v-tippy="{
                        placement: 'top',
                        theme: 'light',
                        arrow: true,
                        animation: 'scale-extreme',
                        duration: [200, 200],
                        hideOnClick: true,
                    }"
                    content="Sürükleyerek taşıyabilirsiniz"
                    >drag_indicator</i
                >
                <widget-title :widget="widget" :table="table" @widget-updated="handleWidgetUpdated" />
            </div>

            <div class="header-actions">
                <div class="view-switcher">
                    <a-button-group size="small">
                        <a-button :type="viewMode === 'chart' ? 'primary' : 'default'" @click="viewMode = 'chart'">
                            <i class="material-icons">bar_chart</i>
                        </a-button>
                        <a-button :type="viewMode === 'list' ? 'primary' : 'default'" @click="viewMode = 'list'">
                            <i class="material-icons">list</i>
                        </a-button>
                    </a-button-group>
                </div>

                <widget-actions
                    @fullscreen="handleFullscreen"
                    @edit="handleEdit"
                    @rename="handleRename"
                    @duplicate="handleDuplicate"
                    @dock="handleDock"
                    @export="handleExport"
                    @delete="$emit('delete', widget._id)"
                    @show-list-view="handleShowListView"
                    :title="widget.title"
                    :widget="widget"
                    :table="table"
                    :showListViewOption="true"
                    @widget-updated="handleWidgetUpdated"
                    @widget-duplicated="handleWidgetDuplicated"
                    @widget-deleted="handleWidgetDeleted"
                />
            </div>
        </div>
        <div class="widget-content">
            <!-- Liste görünümü (inline) -->
            <TableDetail
                v-if="viewMode === 'list'"
                :defaultTableId="table._id"
                :implicitFilters="getImplicitFilters()"
                :liteListMode="true"
                defaultBoardViewMode="table"
                :disableBoardViewChange="true"
                :disableToolbar="true"
                :disableFooter="false"
            />

            <!-- Chart görünümü -->
            <template v-else>
                <empty-chart
                    v-if="!hasValidSettings || !chartData || chartData.error"
                    :error="chartData ? chartData.error : null"
                    :has-valid-settings="hasValidSettings"
                    @edit="handleEdit"
                    :loading="loading"
                />
                <div v-else-if="loading" class="loading-overlay">
                    <loading-wave text="Veriler yükleniyor..." />
                </div>
                <bar-chart
                    v-else-if="widget.settings.chartType === 'bar'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
                <line-chart
                    v-else-if="widget.settings.chartType === 'line' || widget.settings.chartType === 'smooth-line'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
                <stacked-line-chart
                    v-else-if="widget.settings.chartType === 'stacked-line' || widget.settings.chartType === 'stacked-line-100'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
                <area-chart
                    v-else-if="widget.settings.chartType === 'area'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
                <stacked-bar-chart
                    v-else-if="widget.settings.chartType === 'stacked-bar'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
                <stacked-area-chart
                    v-else-if="widget.settings.chartType === 'stacked-area'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
                <pie-chart
                    v-else-if="widget.settings.chartType === 'pie'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
                <donut-chart
                    v-else-if="widget.settings.chartType === 'donut'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
                <bubble-chart
                    v-else-if="widget.settings.chartType === 'bubble'"
                    :data="chartData"
                    :settings="widget.settings"
                    :title="widget.title"
                    :fields="table.fields"
                />
            </template>
        </div>

        <chart-settings-modal
            v-if="showSettingsModal"
            :visible="showSettingsModal"
            :widget="widget"
            :fields="table.fields"
            @close="showSettingsModal = false"
            @save="handleSettingsSave"
            :table="table"
        />

        <Modal2
            v-if="showListModal"
            :title="`${widget.title} - Liste Görünümü`"
            @cancel="showListModal = false"
            :show-footer="false"
            className="widget-list-modal"
            xlarge
        >
            <TableDetail
                :defaultTableId="table._id"
                :implicitFilters="getImplicitFilters()"
                :liteListMode="true"
                defaultBoardViewMode="table"
                :disableBoardViewChange="true"
                :disableToolbar="true"
                :disableFooter="false"

            />
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../../../api/table";
import log2 from "../../../../../../utils/log2";
import WidgetActions from "./components/WidgetActions.vue";
import WidgetTitle from "./components/WidgetTitle.vue";
import EmptyChart from "./components/EmptyChart.vue";
import BarChart from "./components/BarChart.vue";
import LineChart from "./components/LineChart.vue";
import AreaChart from "./components/AreaChart.vue";
import StackedBarChart from "./components/StackedBarChart.vue";
import StackedAreaChart from "./components/StackedAreaChart.vue";
import PieChart from "./components/PieChart.vue";
import DonutChart from "./components/DonutChart.vue";
import BubbleChart from "./components/BubbleChart.vue";
import ChartSettingsModal from "./components/ChartSettingsModal.vue";
import StackedLineChart from "./components/StackedLineChart.vue";
import { debounce } from "lodash";
import LoadingWave from "../../../../../../components/common/LoadingWave.vue";
import Modal2 from "../../../../../../components/common/Modal2.vue";


const log = log2("ChartWidget");

export default Vue.extend({
    name: "ChartWidget",
    components: {
        WidgetActions,
        WidgetTitle,
        EmptyChart,
        BarChart,
        LineChart,
        AreaChart,
        StackedBarChart,
        StackedAreaChart,
        PieChart,
        DonutChart,
        BubbleChart,
        ChartSettingsModal,
        StackedLineChart,
        LoadingWave,
        Modal2,
        TableDetail:() => import("../../TableDetail.vue"),
    },
    props: {
        widget: {
            type: Object,
            required: true,
        },
        upstreamFilters: {
            type: Object,
            default: () => ({filters: [], searchQuery: ""}),
        },
        table: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            chartData: null,
            showSettingsModal: false,
            isEditingTitle: false,
            editingTitle: "",
            loading: false,
            showListModal: false,
            viewMode: 'chart', // 'chart' veya 'list'
        };
    },
    computed: {
        hasValidSettings() {
            const settings = this.widget.settings;
            if (!settings?.chartType) return false;

            // Grafik tipine göre gerekli ayarları kontrol et
            switch(settings.chartType) {
                case 'pie':
                case 'donut':
                    return !!settings.xAxis?.field && (
                        !!settings.yAxis?.calculationType || 
                        settings.yAxis?.calculationType === 'count'
                    );
                case 'stacked-bar':
                case 'stacked-line':
                case 'stacked-area':
                case 'stacked-line-100':
                case 'stacked-area-100':
                case 'stacked-bar-100':
                    return !!settings.xAxis?.field && !!settings.stackBy?.field;
                case 'bubble':
                    return !!settings.xAxis?.field && !!settings.yAxis?.field;
                default:
                    return !!settings.xAxis?.field && (
                        !!settings.yAxis?.calculationType || 
                        settings.yAxis?.calculationType === 'count'
                    );
            }
        },
       
    },
    created() {
        this.debouncedFetchChartData = debounce(async () => {
            try {
                this.loading = true;
                this.chartData = await TABLE_API.getChartData(this.table._id, this.widget._id, this.upstreamFilters);
            } catch (error) {
                console.error("Failed to fetch chart data", error);
                this.$message.error("Grafik verisi yüklenemedi");
            } finally {
                this.loading = false;
            }
        }, 500);
    },
    async mounted() {
        await this.fetchChartData();
    },
    methods: {
        async fetchChartData() {
            await this.debouncedFetchChartData();
        },
        handleFullscreen() {
            // TODO: Tam ekran modunu implement et
        },
        async handleRename(newName) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    title: newName,
                });
                this.$emit("widget-updated", {
                    ...this.widget,
                    title: newName,
                });
            } catch (error) {
                console.error("Widget adı güncellenirken hata:", error);
                throw error;
            }
        },
        async handleDuplicate() {
            try {
                const result = await TABLE_API.duplicateWidget(this.table._id, this.widget.page, this.widget._id);
                this.$message.success("Widget başarıyla çoğaltıldı");
                this.$emit("widget-duplicated", {
                    widget: result.widget,
                    page: result.page,
                });
            } catch (error) {
                console.error("Widget çoğaltılırken hata:", error);
                this.$message.error("Widget çoğaltılırken bir hata oluştu");
            }
        },
        calculateNewPosition() {
            if (!this.layout || !this.layout.length) {
                return { x: 0, y: 0 };
            }

            // Mevcut layout'taki en alt y pozisyonunu bul
            const maxY = Math.max(...this.layout.map((item) => item.y + item.h));

            // Son satırdaki widget'ları bul
            const lastRowWidgets = this.layout.filter((item) => item.y + item.h === maxY);

            // Son satırda boş yer var mı kontrol et
            const totalWidthInLastRow = lastRowWidgets.reduce((sum, item) => sum + item.w, 0);

            if (totalWidthInLastRow + 6 <= 12) {
                // Son satırda yeterli boşluk varsa
                const lastX = Math.max(...lastRowWidgets.map((item) => item.x + item.w), 0);
                return {
                    x: lastX,
                    y: lastRowWidgets[0]?.y || 0,
                };
            }

            // Yeni satıra geç
            return {
                x: 0,
                y: maxY,
            };
        },
        handleDock() {
            // TODO: Sabitleme işlemini implement et
        },
        handleExport() {
            // TODO: Dışa aktarma işlemini implement et
        },
        handleEdit() {
            this.showSettingsModal = true;
        },
        async handleSettingsSave(newSettings) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    settings: newSettings,
                });
                this.$message.success("Grafik ayarları güncellendi");
                this.$emit("widget-updated", {
                    ...this.widget,
                    settings: newSettings,
                });
                this.showSettingsModal = false;
                await this.fetchChartData();
            } catch (error) {
                console.error("Grafik ayarları güncellenirken hata:", error);
                this.$message.error("Grafik ayarları güncellenirken bir hata oluştu");
            }
        },
        async handleWidgetUpdated(updatedWidget) {
            this.$emit("widget-updated", updatedWidget);
        },
        async handleWidgetDuplicated(result) {
            this.$emit("widget-duplicated", result);
        },
        async handleWidgetDeleted(widgetId) {
            this.$emit("delete", widgetId);
        },
        handleShowListView() {
            this.showListModal = true;
        },
        getImplicitFilters() {
            const filters = [];
            
            // Widget'ın kendi filtrelerini ekle
            if (this.widget.settings && this.widget.settings.filters) {
                filters.push(...this.widget.settings.filters);
            }
            
            // Upstream filtrelerini de ekle
            if (this.upstreamFilters && this.upstreamFilters.filters) {
                filters.push(...this.upstreamFilters.filters);
            }
            
            return filters;
        },
    },
    watch: {
        "widget.settings": {
            handler() {
                this.fetchChartData();
            },
            deep: true,
        },
        upstreamFilters: {
            handler() {
                this.fetchChartData();
            },
            deep: true,
        },
    },
    beforeDestroy() {
        if (this.debouncedFetchChartData) {
            this.debouncedFetchChartData.cancel();
        }
    },
});
</script>

<style scoped lang="scss">
.chart-widget {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    border: 2px solid #fafafa;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: #1890ff;

        .drag-handle {
            visibility: visible;
            line-height: 1px;
        }
    }

    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.8);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 2;
    }

    .drag-handle {
        transform: translateY(-50%);
        font-size: 20px;
        color: rgba(0, 0, 0, 0.25);
        cursor: move;
        transition: opacity 0.2s, color 0.2s, left 0.2s;
        visibility: hidden;
        line-height: 1px;
        &:hover {
            color: rgba(0, 0, 0, 0.45);
            left: -8px;
        }
    }

    .widget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
        background: #fafafa;
        position: relative;
        z-index: 10;
        .left {
            display: flex;
            align-items: center;
            gap: 5px;
            width: -webkit-fill-available;
        }

        .header-actions {
            display: flex;
            align-items: center;
            gap: 8px;

            .view-switcher {
                :deep(.ant-btn-group) {
                    .ant-btn {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 4px 8px;
                        height: 32px;

                        .material-icons {
                            font-size: 18px;
                        }
                    }
                }
            }
        }
    }

    .widget-content {
        flex: 1;
        min-height: 0;
        position: relative;
        z-index: 1;

    }
}
</style>

<style lang="scss">
.widget-list-modal {
    .ant-modal {
        .custom-modal-body {
            padding: 0 !important;
            height: 70vh;
            overflow: hidden;
        }
    }
}
</style>
